// Helpers for building responsive image URLs against the /api/assets and
// /api/image-proxy resize endpoints. The explicit f=webp param bakes the
// output format into the URL (no Accept-header negotiation, which is
// unreliable behind Cloudflare's caching).

function isResizableUrl(url: string): boolean {
  return url.startsWith("/api/assets/") || url.startsWith("/api/image-proxy");
}

/**
 * Return the image URL sized to the given width/quality as WebP.
 * Non-resizable (external) URLs are returned unchanged.
 */
export function sizedImageUrl(url: string, w: number, q = 80): string {
  if (!url || !isResizableUrl(url)) return url;
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.set("w", String(w));
  params.set("q", String(q));
  params.set("f", "webp");
  // Encode raw spaces — a space breaks srcset parsing (it's the candidate
  // delimiter). Only spaces are replaced to avoid double-encoding URLs that
  // are already percent-encoded.
  return `${base.split(" ").join("%20")}?${params.toString()}`;
}

/**
 * Build a srcset string for the given widths. Returns undefined for
 * non-resizable URLs so the plain src is used instead.
 */
export function sizedImageSrcSet(
  url: string,
  widths: number[],
  q = 80
): string | undefined {
  if (!url || !isResizableUrl(url)) return undefined;
  return widths.map((w) => `${sizedImageUrl(url, w, q)} ${w}w`).join(", ");
}
